"use client";

import React from "react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Activity, Target, Zap, ChevronRight, ShieldCheck, ChartBar } from "lucide-react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const features = [{
  icon: Target,
  title: "Doctor Verified",
  description: "Direct integration with Atlanta's top medical practices."
}, {
  icon: Zap,
  title: "Swift Delivery",
  description: "Same-day secure transport from pharmacy to your door."
}, {
  icon: Activity,
  title: "Adherence Tracking",
  description: "Real-time monitoring to ensure patient compliance."
}];
export default function ScoutingNouvelleGeneration() {
  const {
    ref: textRef,
    isVisible: textVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const {
    ref: visualRef,
    isVisible: visualVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section data-section-id="239510" id="empowering-your-practice" className="bg-background text-foreground relative overflow-hidden py-24 lg:py-32">
      {}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full h-full max-w-[1200px] pointer-events-none">
        <div className="absolute top-1/4 left-0 w-96 h-96 bg-primary/5 rounded-lg blur-[120px] opacity-40" />
        <div className="absolute bottom-1/4 right-0 w-96 h-96 bg-secondary/10 rounded-lg blur-[120px] opacity-40" />
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          
          {}
          <div ref={textRef} className={`flex flex-col gap-8 transition-all duration-1000 ease-out ${textVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
            <div>
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-lg bg-primary/10 text-primary text-sm font-medium mb-6 border border-primary/20">
                <ShieldCheck className="size-4" />
                <span>For Atlanta Medical Professionals</span>
              </div>
              <h1 className="font-default text-5xl lg:text-6xl xl:text-7xl font-bold tracking-tight leading-[1.1] mb-6">
                Empowering Your <br />
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary to-secondary">
                  Medical Practice.
                </span>
              </h1>
              <p className="text-muted-foreground text-lg lg:text-xl max-w-xl leading-relaxed font-default">
                Reduce administrative burden and improve medication adherence. GOTMEDS.IO provides a premium, secure logistics bridge between your clinic and your patients' homes.
              </p>
            </div>

            <div className="flex flex-col sm:flex-row gap-4">
              <Button asChild size="lg" className="h-14 px-8 text-base rounded-lg shadow-lg shadow-primary/20 bg-gradient-to-r from-primary to-primary/90 hover:opacity-90 transition-opacity">
                <Link to="/PatientSupportPage">
                  Request a Demo
                  <ChevronRight className="ml-2 size-5" />
                </Link>
              </Button>
              <Button asChild variant="outline" size="lg" className="h-14 px-8 text-base rounded-lg bg-background/50 backdrop-blur-sm border-border/50 hover:bg-accent">
                <Link to="/CareCyclePage">
                  The Care Cycle
                </Link>
              </Button>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 pt-8 border-t border-border/50 mt-4">
              {features.map((feature, i) => <div key={i} data-index={i} className="flex flex-col gap-2">
                  <div className="size-10 rounded-lg bg-accent flex items-center justify-center text-primary mb-2">
                    <feature.icon className="size-5" />
                  </div>
                  <h3 className="font-default font-semibold text-foreground">{feature.title}</h3>
                  <p className="text-sm text-muted-foreground font-default">{feature.description}</p>
                </div>)}
            </div>
          </div>

          {}
          <div ref={visualRef} className={`relative transition-all duration-1000 delay-200 ease-out ${visualVisible ? "opacity-100 translate-x-0" : "opacity-0 translate-x-12"}`}>
            {}
            <div className="relative rounded-2xl overflow-hidden border border-border/50 shadow-2xl shadow-primary/5 bg-card/40 backdrop-blur-xl aspect-[4/3] flex items-center justify-center p-2">
              <div className="absolute inset-0 bg-gradient-to-br from-primary/5 to-transparent pointer-events-none" />
              
              {}
              <div className="absolute top-0 left-0 w-full h-12 bg-card/90 backdrop-blur-md border-b border-border/50 flex items-center px-4 gap-2 z-20">
                <div className="flex gap-1.5">
                  <div className="size-3 rounded-lg bg-destructive/40" />
                  <div className="size-3 rounded-lg bg-secondary/40" />
                  <div className="size-3 rounded-lg bg-primary/40" />
                </div>
                <div className="mx-auto h-6 w-48 bg-background/50 rounded-md border border-border/50 flex items-center justify-center">
                  <span className="text-[10px] text-muted-foreground font-mono">GOTMEDS.IO</span>
                </div>
              </div>

              {}
              <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1651003/img/atlanta_clinic_dashboard_interface.png" alt="Professional medical dashboard showing prescription tracking and patient adherence metrics" className="w-full h-full object-cover rounded-xl mt-10 opacity-95" />

              {}
              <Card className="absolute -left-6 top-1/4 w-48 bg-card/90 backdrop-blur-xl border-border/50 shadow-xl shadow-primary/10 hidden md:flex flex-col animate-[bounce_4s_infinite] rounded-lg">
                <CardHeader className="pb-2 pt-4 px-4">
                  <CardTitle className="text-xs text-muted-foreground flex items-center gap-2 font-default">
                    <ChartBar className="size-3 text-primary" />
                    Adherence Rate
                  </CardTitle>
                </CardHeader>
                <CardContent className="px-4 pb-4">
                  <div className="text-3xl font-default font-bold text-foreground">98.4<span className="text-sm text-secondary ml-1">%</span></div>
                  <div className="w-full bg-muted h-1.5 rounded-lg mt-3 overflow-hidden">
                    <div className="bg-secondary h-full w-[98%]" />
                  </div>
                </CardContent>
              </Card>

              {}
              <Card className="absolute -right-8 bottom-1/4 w-56 bg-card/90 backdrop-blur-xl border-border/50 shadow-xl shadow-primary/10 hidden md:flex flex-col animate-[bounce_5s_infinite_reverse] rounded-lg">
                <CardHeader className="pb-2 pt-4 px-4">
                  <CardTitle className="text-xs text-muted-foreground flex items-center gap-2 font-default">
                    <Target className="size-3 text-secondary" />
                    Active Delivery
                  </CardTitle>
                </CardHeader>
                <CardContent className="px-4 pb-4 flex items-center gap-3">
                  <div className="size-10 rounded-lg bg-accent overflow-hidden border border-border">
                    <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1651003/img/atlanta_courier_avatar.png" alt="Courier" className="w-full h-full object-cover" />
                  </div>
                  <div>
                    <div className="text-sm font-default font-semibold text-foreground">Route #404</div>
                    <div className="text-xs text-muted-foreground font-mono">ETA: 12 MINS</div>
                  </div>
                </CardContent>
              </Card>

            </div>
          </div>

        </div>
      </div>
    </section>;
}